import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"

import "regenerator-runtime/runtime"

import Head from "next/head"
import { useBottomEdgeInset } from "@/hooks/use-bottom-edge-inset"
import { Session } from "@/types/auth"
import { PublicPageProps } from "@/types/page"
import { Honeybadger } from "@honeybadger-io/react"
import isEqual from "lodash/isEqual"
import { Provider } from "react-redux"

import { getUtmParams } from "@/lib/analytics/amplitude-utils"
import { optOutGTMTracking } from "@/lib/analytics/gtm-utils"
import {
  broadcastAuthEvent,
  subscribeToAuthEvents,
} from "@/lib/auth/broadcast-channel"
import { sessionRefreshEmitter } from "@/lib/auth/session-refresh-emitter"
import { buildClientAxios } from "@/lib/client-axios"
import { useAmplitudeInit } from "@/lib/hooks/use-amplitude-init"
import { useAppInit } from "@/lib/hooks/use-app-init"
import { useUtmTracking } from "@/lib/hooks/use-utm-tracking"
import { TranslationsProvider } from "@/lib/i18n/i18n-context"
import { mtLocation } from "@/lib/mt-location"
import { useRouter } from "@/lib/router"
import { links } from "@/lib/routes"
import { setViewSize } from "@/lib/storage/app-slice"
import {
  applyPageProps,
  initApp,
  storeSession,
  updateSessionFromRefresh,
} from "@/lib/storage/app-thunks"
import { selectSession, selectSessionStatus } from "@/lib/storage/auth-slice"
import { showRegisterGateDialog } from "@/lib/storage/auth-thunks"
import { registerCalendarSyncStore } from "@/lib/storage/calendar-sync-runtime"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { beginPageInitCycle } from "@/lib/storage/page-init-gate"
import { AppStore, buildStore } from "@/lib/storage/redux-store"
import { setMobileNavVisible } from "@/lib/storage/site-nav-slice"
import { selectChatId } from "@/lib/storage/user-slice"
import { UserApi } from "@/lib/user-api"
import { BASE_CANONICAL_URL } from "@/lib/utils/constants"
import { RELEASE_VERSION } from "@/lib/version"
import { ActivityMonitor } from "@/components/activity-monitor"
import { GtmNoScript } from "@/components/analytics/gtm-noscript"
import { GtmScript } from "@/components/analytics/gtm-script"
import { AudioGuidePlayerHost } from "@/components/audio-guide-player-host"
import { AuthDialog } from "@/components/auth/auth-dialog"
import { ClientRouteHandler } from "@/components/client-route-handler"
import { DialogInfo } from "@/components/dialog-info"
import { EmbedClose } from "@/components/embedded/embed-close"
import { Fonts } from "@/components/fonts"
import { GeoInit } from "@/components/geo-init"
import { LoadSettings } from "@/components/load-settings"
import { LocaleHandler } from "@/components/locale-handler"
import { MindtripErrorBoundary } from "@/components/mindtrip-error-boundary"
import { NewTabClickGuard } from "@/components/new-tab-click-guard"
import { ObtrusiveScrollbarTest } from "@/components/obtrusive-scrollbar-test"
import { PerfReportLauncher } from "@/components/perf-report/perf-report-launcher"
import { PresenceDetector } from "@/components/presence-detector"
import { SavedContentSyncProvider } from "@/components/saved-content-sync-provider"
import { TripSyncProvider } from "@/components/trip-sync-provider"
import { Toaster } from "@/components/ui/toast"
import { TooltipProvider } from "@/components/ui/tooltip"
import { UnhandledErrorComponent } from "@/components/unhandled-error-component"
import { ViewSizeHandler } from "@/components/view-size-handler"
import { VoiceChat } from "@/components/voice-chat"

export type WebAppProps = PublicPageProps

const GENERIC_OG_IMAGE = `${BASE_CANONICAL_URL}/img/og-generic.jpg`

function HBContext({ initUser }: { initUser?: { uuid?: string } }) {
  const session = useAppSelector(selectSession)
  const chatId = useAppSelector(selectChatId)

  useEffect(() => {
    Honeybadger.setContext({
      user_uuid: initUser?.uuid ?? session?.user?.uuid,
      chatId: chatId || undefined,
      version: RELEASE_VERSION,
    })
  }, [session, chatId, initUser])

  return null
}

// Only the props applyPageProps reads, plus the path: a landing re-checks the
// post-signup prompts. Page data (a trip, a place) stays out, so this is not a
// deep compare of the page on every render.
function appPropsSnapshot(props: PublicPageProps, path: string): unknown {
  let session: unknown = undefined
  if (props.session) {
    const { expires_in: _, ...stableSession } = props.session
    session = stableSession
  }
  return {
    session,
    enabledFeatureFlags: props.enabledFeatureFlags,
    earlyAccessFlags: props.earlyAccessFlags,
    organization: props.organization,
    orgid: props.orgid,
    orgUrl: props.orgUrl,
    geoName: props.geoName,
    path,
  }
}

export function Bootstrap(props: PublicPageProps) {
  const prevRef = useRef<unknown>(null)
  const appInitRef = useRef(false)
  const dispatch = useAppDispatch()
  const router = useRouter()
  const status = useAppSelector(selectSessionStatus)

  useEffect(() => {
    if (!appInitRef.current) {
      appInitRef.current = true
      void dispatch(initApp(props))
    }

    const snapshot = appPropsSnapshot(props, router.asPath.split(/[?#]/)[0])
    if (!isEqual(prevRef.current, snapshot)) {
      prevRef.current = snapshot
      // Start a fresh gate before applyPageProps so route effects cannot
      // observe a stale resolved gate on later page-prop changes.
      beginPageInitCycle()

      void dispatch(applyPageProps(props))

      // If we get special utm_term=uccl:0, we want to gate the user right away
      // For some reason, status is now giving me "loading" instead of "unauthenticated"
      // So I added props.session?.user?.provider === "anonymous" also
      const isAnonymous =
        status === "unauthenticated" ||
        props.session?.user?.provider === "anonymous"
      if (isAnonymous) {
        const { query } = router
        if (
          /^\/chat\/?/.test(router.asPath) ||
          /^\/location\/?/.test(router.asPath) ||
          /^\/inspiration\/?/.test(router.asPath)
        ) {
          const utmProperties = getUtmParams(query)
          if (utmProperties.utmTerm === "uccl:0") {
            void dispatch(showRegisterGateDialog("chat-0-message"))
          }
        }
      }
    }
  })

  // Listen for session refresh events to update Redux state
  useEffect(() => {
    const handleSessionRefresh = (session: Session) => {
      void dispatch(updateSessionFromRefresh(session))
    }

    sessionRefreshEmitter.addListener(handleSessionRefresh)

    return () => {
      sessionRefreshEmitter.removeListener(handleSessionRefresh)
    }
  }, [dispatch])

  // Broadcast current session on mount and listen for auth events from other tabs
  useEffect(() => {
    // Broadcast current session_id so other tabs know if something changed
    if (props.session?.session_id) {
      broadcastAuthEvent({
        type: "session-check",
        session_id: props.session.session_id,
      })
    }

    // Listen for auth events from other tabs
    return subscribeToAuthEvents((event) => {
      switch (event.type) {
        case "auth-signin":
        case "auth-register":
        case "auth-create-anon":
          // Reload page when auth state changes
          setTimeout(() => mtLocation.reload(), 100)
          break
        case "auth-signout":
          {
            // Handle sign-out based on type
            const thisSession = props.session
            setTimeout(() => {
              if (
                event.signout_type === "user" ||
                thisSession == null ||
                thisSession?.user?.provider === "anonymous"
              ) {
                mtLocation.replace("/")
              } else {
                const callbackUrl = mtLocation.pathname + mtLocation.search
                mtLocation.replace(
                  links.signIn(
                    callbackUrl.startsWith("/auth") ? "/" : callbackUrl
                  )
                )
              }
            }, 100)
          }
          break
        case "auth-refresh":
          // Update session without reload
          if (event.session) {
            void dispatch(updateSessionFromRefresh(event.session))
          }
          break
        case "session-check":
          // If session_id changed, reload
          if (event.session_id !== props.session?.session_id) {
            setTimeout(() => mtLocation.reload(), 100)
          }
          break
      }
    })
  }, [props.session, dispatch])

  return null
}

export default function WebApp({
  children,
  ...pageProps
}: WebAppProps & {
  children: ReactNode
}) {
  const router = useRouter()
  const userApi = useMemo(() => new UserApi(buildClientAxios()), [])
  const storeRef = useRef<AppStore | null>(null)
  if (!storeRef.current) {
    storeRef.current = buildStore()
    // The store starts with what the server renders with, so a page body
    // renders the same on the server and in hydration: the view size it
    // guessed from the user agent (ViewSizeHandler corrects a wrong guess) and
    // the session (applyPageProps stores it again on landing).
    if (pageProps.viewSize) {
      storeRef.current.dispatch(setViewSize(pageProps.viewSize))
      // A phone's tab bar is in the server HTML, shown; SiteNavContent keeps
      // the flag in step with the view size after mount.
      storeRef.current.dispatch(
        setMobileNavVisible(pageProps.viewSize === "xs")
      )
    }
    if (pageProps.session) {
      storeSession(storeRef.current.dispatch, pageProps.session)
    }
    registerCalendarSyncStore(storeRef.current)
  }
  const session = pageProps.session

  // Soft navigations omit translations (see MtSSP); keep the document's.
  const [translations, setTranslations] = useState(pageProps.translations)
  if (pageProps.translations && pageProps.translations !== translations) {
    setTranslations(pageProps.translations)
  }

  // Use shared hooks for common initialization
  useAmplitudeInit({
    session,
    organization: pageProps.organization,
    pathname: router.pathname,
    query: router.query,
  })

  useAppInit({
    session,
    supportedBrowser: pageProps.supportedBrowser,
  })

  // Owns --bottom-inset on its readers — see lib/bottom-edge/writer.ts.
  useBottomEdgeInset()

  useUtmTracking({
    session,
    query: router.query,
    userApi,
  })

  const relativeCanonicalUrl = router.asPath.split("?")[0]
  const canonicalUrl =
    relativeCanonicalUrl !== "/"
      ? BASE_CANONICAL_URL + relativeCanonicalUrl
      : BASE_CANONICAL_URL

  const optOut = session?.user?.functional_cookies_enabled === false

  optOutGTMTracking(optOut)

  return (
    <>
      <Head>
        <title>mindtrip</title>
        <link rel="canonical" href={canonicalUrl} key="canonical" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover"
        />
        <meta property="og:image" content={GENERIC_OG_IMAGE} key="og-image" />
        <meta property="og:type" content="website" key="og-type" />
        <meta property="og:url" content={canonicalUrl} key="og-url" />
        <meta property="og:site_name" content="Mindtrip" />
        <meta name="twitter:site" content="@mindtripai" key="tw-site" />
        <GtmScript supportedBrowser={pageProps.supportedBrowser} />
      </Head>
      <MindtripErrorBoundary
        honeybadger={Honeybadger}
        ErrorComponent={UnhandledErrorComponent}
      >
        <Provider store={storeRef.current}>
          <GtmNoScript supportedBrowser={pageProps.supportedBrowser} />
          <ActivityMonitor />
          <ViewSizeHandler />
          <NewTabClickGuard />
          <PresenceDetector />
          <GeoInit />
          <SavedContentSyncProvider />
          <TripSyncProvider />
          <LoadSettings initSettings={pageProps.initSettings ?? null} />
          <Bootstrap {...pageProps} />
          <ClientRouteHandler initUser={session?.user} />
          <HBContext initUser={session?.user} />
          <ObtrusiveScrollbarTest />
          <Fonts />
          <LocaleHandler />
          <TooltipProvider>
            <TranslationsProvider value={translations}>
              {children}
              {pageProps.isEmbedded && <EmbedClose />}
            </TranslationsProvider>
          </TooltipProvider>
          <Toaster />
          <AudioGuidePlayerHost />
          {/* Root-mounted so a live voice session — surfaces and audio sink
            alike — outlives the chat page it started on. */}
          <VoiceChat />
          <DialogInfo />
          <AuthDialog />
          <PerfReportLauncher />
        </Provider>
      </MindtripErrorBoundary>
    </>
  )
}

// https://nextjs.org/docs/advanced-features/measuring-performance
//
// This might be useful in the future.  Especially if we send the metric to analytics
//
// export function reportWebVitals(metric: NextWebVitalsMetric) {
//   console.log(metric)
// }
